import type { SolomonAgentMessage } from 'hooks/solomon/useSolomonAgent';

import { ImageMessage } from './ImageMessage';
import type { RegenerateOption } from './RegenerateOptions';
import { formatToolName, extractImageUrl, IMAGE_TOOLS } from '../utils/conversationUtils';
import { type SlotType, PROFILE_SLOTS } from '../utils/imageUtils';

type ToolUse = NonNullable<SolomonAgentMessage['toolUses']>[0];

type ImageToolState = {
  toolCallId: string;
  imageUrl: string;
  targetSlot: SlotType;
  isSaved: boolean;
  isSaving: boolean;
};

type MessageToolUsesProps = {
  messageId: string;
  toolUses: ToolUse[];
  imageToolStates: Record<string, ImageToolState>;
  showRegenerateFor: string | null;
  extractTargetSlot: (input?: Record<string, unknown>, userPrompt?: string) => SlotType;
  userPrompt: string;
  onImageSave: (toolCallId: string, imageUrl: string, targetSlot: SlotType) => void;
  onImageReject: (toolCallId: string) => void;
  onRegenerateSelect: (option: RegenerateOption, toolCallId: string) => void;
  onCustomPrompt: () => void;
  styles: Record<string, string>;
};

function isImageToolResult(toolUse: ToolUse): boolean {
  return IMAGE_TOOLS.has(toolUse.tool) && !!toolUse.result && !toolUse.isError;
}

export function MessageToolUses({
  messageId,
  toolUses,
  imageToolStates,
  showRegenerateFor,
  extractTargetSlot,
  userPrompt,
  onImageSave,
  onImageReject,
  onRegenerateSelect,
  onCustomPrompt,
  styles,
}: MessageToolUsesProps) {
  const nonImageToolUses = toolUses.filter((t) => !isImageToolResult(t));
  const imageToolUses = toolUses.filter(isImageToolResult);

  return (
    <div className={styles.toolUses}>
      {/* Non-image tool indicators */}
      {nonImageToolUses.map((toolUse) => (
        <div key={`${messageId}-tool-${toolUse.toolCallId}`} className={styles.toolUse}>
          <span className={styles.toolIcon}>{toolUse.isError ? '!' : '+'}</span>
          <span className={styles.toolName}>{formatToolName(toolUse.tool)}</span>
          {toolUse.result && !toolUse.isError && <span className={styles.toolSuccess}>completed</span>}
          {toolUse.isError && <span className={styles.toolError}>failed</span>}
        </div>
      ))}

      {/* Image tools */}
      {imageToolUses.map((toolUse) => {
        const imageUrl = extractImageUrl(toolUse.result);
        const targetSlot = extractTargetSlot(toolUse.input, userPrompt);
        const state = imageToolStates[toolUse.toolCallId];

        if (!imageUrl) return null;

        if (showRegenerateFor === toolUse.toolCallId) {
          return (
            <div key={`regen-${toolUse.toolCallId}`} className={styles.rejectConversation}>
              <p className={styles.rejectQuestion}>What would you like me to do instead?</p>
              <div className={styles.prompts}>
                <button
                  type="button"
                  className={styles.promptOption}
                  onClick={() =>
                    onRegenerateSelect(
                      {
                        id: 'retry',
                        label: 'Try Again',
                        icon: '🔄',
                        prompt: 'Generate a new variation with the same settings',
                      },
                      toolUse.toolCallId
                    )
                  }
                >
                  <span className={styles.promptNumber}>1.</span> Generate a new version
                </button>
                <button
                  type="button"
                  className={styles.promptOption}
                  onClick={() =>
                    onRegenerateSelect(
                      {
                        id: 'style',
                        label: 'Different Style',
                        icon: '🖼️',
                        prompt: 'Generate with a completely different artistic style',
                      },
                      toolUse.toolCallId
                    )
                  }
                >
                  <span className={styles.promptNumber}>2.</span> Try a different style
                </button>
                <button type="button" className={styles.promptOption} onClick={onCustomPrompt}>
                  <span className={styles.promptNumber}>3.</span> Let me describe what I want
                </button>
              </div>
              <p className={styles.rejectHint}>Or just type what you'd like...</p>
            </div>
          );
        }

        return (
          <ImageMessage
            key={`${messageId}-img-${toolUse.toolCallId}`}
            imageUrl={imageUrl}
            targetSlot={targetSlot}
            isSaved={state?.isSaved || false}
            isSaving={state?.isSaving || false}
            onSave={() => onImageSave(toolUse.toolCallId, imageUrl, targetSlot)}
            onReject={() => onImageReject(toolUse.toolCallId)}
            message={`Here's the ${PROFILE_SLOTS[targetSlot]?.shortLabel || 'profile'} image I generated:`}
          />
        );
      })}
    </div>
  );
}
